import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card as CardAtom, CustomSpinner } from "_atoms";
import { selectIata } from "_redux/slices/main-screen/user";
import {
  getDepartureBookings,
  selectCurrentHourCount,
  selectNextHourCount,
  selectSecurityStatus,
  selectDeparturesByInterval,
  selectUpcomingFlights,
} from "_redux/slices/security/security";
import {
  ColGrid,
  Flex,
  Block,
  Text,
  Metric,
  Title,
  Subtitle,
  Toggle,
  ToggleItem,
  Col as TremorCol,
} from "@tremor/react";
import { BarChart } from "@tremor/react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Colors } from "_styles";

const zeroPad = (n) => n.toString().padStart(2, "0");

const getHourLabel = (hour) =>
  `${zeroPad(hour)}:00 – ${zeroPad(hour + 1)}:00`;

const SecurityAnalytics = () => {
  const dispatch = useDispatch();
  const iata = useSelector(selectIata);
  const [mode, setMode] = useState("Real Time");
  const [intervalMins, setIntervalMins] = useState(60);

  const now = new Date().getHours();
  const currentHourCount = useSelector(selectCurrentHourCount(mode));
  const nextHourCount = useSelector(selectNextHourCount(mode));
  const intervalSelector = useMemo(
    () => selectDeparturesByInterval(mode, intervalMins),
    [mode, intervalMins]
  );
  const graphData = useSelector(intervalSelector);
  const upcomingFlights = useSelector(selectUpcomingFlights);
  const status = useSelector(selectSecurityStatus);
  const isLoading = status === "loading";

  useEffect(() => {
    if (iata) {
      dispatch(getDepartureBookings({ iata }));
    }
  }, [iata]);

  const currentLabel = mode === "Real Time"
    ? `${getHourLabel(now)}`
    : `${getHourLabel(now)}`;
  const nextLabel = mode === "Real Time"
    ? `${getHourLabel(now + 1)}`
    : `${getHourLabel(now + 1)}`;

  return (
    <>

      {/* flight time and real time toggle */}
      <Row className="mt-3 justify-content-end">
        <Col xs={2}>
          <Toggle
            color="zinc"
            defaultValue={mode}
            onValueChange={(value) => setMode(value)}
          >
            <ToggleItem value="Real Time" text="Real Time" />
            <ToggleItem value="Flight Time" text="Flight Time" />
          </Toggle>
        </Col>
      </Row>

      {isLoading ? (
        <CustomSpinner rowCenter={true} />
      ) : (
        <>
          {/* current hour and next hour cards */}
          <Row>
            <Col xs={4}>
              <CardAtom shadow={true} decoration="top" decorationColor="blue">
                <Flex alignItems="items-start">
                  <Block>
                    <Text>{'Current Hour ' + '(' + mode + ')'}</Text>
                    <Metric>{currentHourCount.toLocaleString()}</Metric>
                    <Text>{currentLabel}</Text>
                  </Block>
                </Flex>
              </CardAtom>
            </Col>

            <Col xs={4}>
              <CardAtom shadow={true} decoration="top" decorationColor="blue">
                <Flex alignItems="items-start">
                  <Block>
                    <Text>{"Next Hour " + '(' + mode + ')'}</Text>
                    <Metric>{nextHourCount.toLocaleString()}</Metric>
                    <Text>{nextLabel}</Text>
                  </Block>
                </Flex>
              </CardAtom>
            </Col>

          </Row>

          {/* departures graph */}
          <ColGrid
            numColsMd={1}
            numColsLg={6}
            gapX="gap-x-6"
            gapY="gap-y-6"
            marginTop="mt-6"
          >
            <TremorCol numColSpanLg={6}>
              <CardAtom>
                <Row>
                  <Col>
                    <Flex>
                      <div style={{ marginLeft: '28px' }}>
                        <Subtitle>
                          {mode === "Real Time"
                            ? "Estimated PRM Security Time"
                            : "Scheduled PRM Departure Time"}
                        </Subtitle>
                      </div>
                      <Toggle
                        color="zinc"
                        defaultValue={String(intervalMins)}
                        onValueChange={(value) => setIntervalMins(Number(value))}
                      >
                        <ToggleItem value="15" text="15 Min" />
                        <ToggleItem value="30" text="30 Min" />
                        <ToggleItem value="60" text="1 Hour" />
                      </Toggle>
                    </Flex>
                  </Col>
                </Row>
                <Row>
                  <BarChart
                    data={graphData}
                    dataKey="name"
                    categories={["Departures"]}
                    colors={["blue"]}
                    marginTop="mt-5"
                    yAxisWidth="w-12"
                    showAnimation={false}
                    valueFormatter={(n) =>
                      Intl.NumberFormat("us").format(n).toString()
                    }
                  />
                </Row>
              </CardAtom>
            </TremorCol>
          </ColGrid>

          {/* departures by flight table */}
          <ColGrid
            numColsMd={1}
            numColsLg={6}
            gapX="gap-x-6"
            gapY="gap-y-6"
            marginTop="mt-6"
          >
            <TremorCol numColSpanLg={6}>
              <CardAtom shadow={true} decoration="top" decorationColor="blue">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    <thead>
                      <tr style={styles.headerRow}>
                        <th style={styles.th}>Flight</th>
                        <th style={styles.th}>Destination</th>
                        <th style={styles.th}>STD</th>
                        <th style={styles.th}>PST</th>
                        <th style={styles.th}>PRMs</th>
                        <th style={styles.th}>WCHC</th>
                        <th style={styles.th}>Load</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingFlights.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ ...styles.td, textAlign: "center", color: "#999" }}>
                            No upcoming flights
                          </td>
                        </tr>
                      ) : (
                        upcomingFlights.map((f, i) => (
                          <tr key={f.flight + i} style={i % 2 === 1 ? styles.altRow : {}}>
                            <td style={styles.td}>{f.flight}</td>
                            <td style={styles.td}>{f.destination}</td>
                            <td style={styles.td}>{f.std}</td>
                            <td style={{ ...styles.td, color: "#307FE2", fontWeight: 600 }}>{f.pst}</td>
                            <td style={styles.td}>{f.prms}</td>
                            <td style={{ ...styles.td, fontWeight: 600 }}>{f.wchc}</td>
                            <td style={styles.td}>
                              <span style={{ ...styles.badge, ...loadColors[f.load] }}>
                                {f.load}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardAtom>
            </TremorCol>
          </ColGrid>
        </>
      )}
    </>
  );
};

const loadColors = {
  High: { backgroundColor: "#fce4e4", color: "#c0392b" },
  Medium: { backgroundColor: "#fef3e2", color: "#e67e22" },
  Low: { backgroundColor: "#e8f8f0", color: "#27ae60" },
};

const styles = {
  headerRow: {
    backgroundColor: Colors.PRM_LIGHT_BLUE,
  },
  th: {
    padding: "12px 16px",
    color: "#fff",
    fontWeight: 600,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #f0f0f0",
    whiteSpace: "nowrap",
  },
  altRow: {
    backgroundColor: "#fafafa",
  },
  badge: {
    padding: "4px 14px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    display: "inline-block",
  },
};

export default SecurityAnalytics;
